import { applyDesignerCommand, designCutoutAssetId, type DesignImageLayer } from '@pz/scene';
import { el, textBtn, type DesignerCtx } from './panels.js';
import { canCropInline, inlineImageRect, inlinePan, inlineResize, inlineZoom, type InlineCropState } from './inline-crop-math.js';

/**
 * Zazzle's inline crop: the photo is cropped where it sits.
 *
 * The whole photograph appears on the canvas, dimmed outside the layer's box;
 * the box becomes a window with eight handles. Dragging inside the window
 * slides the photo, the wheel (or a pinch) zooms it, the handles reshape the
 * window. Nothing is written until Primeni — then the box and the crop land as
 * ONE undo step through the shared commands — and Otkaži or Escape leaves the
 * design exactly as it was. "Više opcija" hands over to the full crop dialog
 * (aspect ratios, masks, straightening) for what a window cannot express.
 */
export interface InlineCropHost {
  ctx: DesignerCtx;
  /** The canvas stage the overlay covers. */
  stage: HTMLElement;
  /** The zone's top-left on the stage and CSS px per mm, right now. */
  geometry(): { left: number; top: number; scale: number };
  /** Hides (or shows again) the layer's own raster while its crop is edited. */
  hideLayer(id: string, hidden: boolean): void;
  /** The full dialog, for "Više opcija". */
  openDialog(id: string): void;
}

export interface InlineCropHandle {
  /** Re-places the overlay after the canvas zoomed, panned or resized. */
  relayout(): void;
  close(apply: boolean): void;
}

export function openInlineCrop(host: InlineCropHost, id: string, onClosed: () => void): InlineCropHandle | undefined {
  const { ctx } = host;
  const original = ctx.store.value().layers.find((item) => item.id === id);
  if (original?.kind !== 'image' || !original.sourcePx || !canCropInline(original)) return undefined;
  const layer: DesignImageLayer = original;
  const source = original.sourcePx;
  const flipX = Boolean(layer.flipX);
  const flipY = Boolean(layer.flipY);
  const url = (layer.effects?.some((effect) => effect.kind === 'removeBackground')
    ? ctx.renderer.assetUrl(designCutoutAssetId(layer.assetId)) : undefined) ?? ctx.renderer.assetUrl(layer.assetId);
  if (!url) return undefined;

  let state: InlineCropState = { box: { ...layer.box }, crop: { cx: 0.5, cy: 0.5, zoom: 1, ...layer.crop } };
  let closed = false;

  const overlay = el('div', 'pzd-icrop');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', ctx.texts.cropTool);
  const picture = el('img', 'pzd-icrop-pic');
  picture.src = url;
  picture.alt = '';
  picture.draggable = false;
  if (flipX || flipY) picture.style.transform = `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`;
  const frame = el('div', 'pzd-icrop-frame');
  frame.tabIndex = 0;
  frame.setAttribute('role', 'group');
  frame.setAttribute('aria-label', ctx.texts.cropWindow);
  frame.append(el('div', 'pzd-crop-thirds'));
  for (const handle of ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']) {
    const button = el('button', 'pzd-icrop-handle');
    button.type = 'button';
    button.dataset.handle = handle;
    button.tabIndex = -1;
    button.setAttribute('aria-hidden', 'true');
    frame.append(button);
  }
  const bar = el('div', 'pzd-icrop-bar');
  const cancel = textBtn(ctx.texts.cancel, () => close(false));
  const more = textBtn(ctx.texts.cropMore, () => {
    close(false);
    host.openDialog(id);
  });
  const apply = textBtn(ctx.texts.phApply, () => close(true), { className: 'pzd-primary' });
  bar.append(cancel, more, apply);
  overlay.append(picture, frame, bar);
  host.stage.append(overlay);
  host.hideLayer(id, true);

  function place(): void {
    const { left, top, scale } = host.geometry();
    const image = inlineImageRect(source, state, flipX, flipY);
    picture.style.left = `${left + image.left * scale}px`;
    picture.style.top = `${top + image.top * scale}px`;
    picture.style.width = `${image.w * scale}px`;
    picture.style.height = `${image.h * scale}px`;
    const box = state.box;
    frame.style.left = `${left + (box.x - box.w / 2) * scale}px`;
    frame.style.top = `${top + (box.y - box.h / 2) * scale}px`;
    frame.style.width = `${box.w * scale}px`;
    frame.style.height = `${box.h * scale}px`;
  }

  // ── Gestures ──────────────────────────────────────────────────────────────
  type Drag = { pointer: number; x: number; y: number; start: InlineCropState; handle: string };
  let drag: Drag | undefined;
  const pinch = new Map<number, { x: number; y: number }>();
  let pinchStart: { distance: number; state: InlineCropState } | undefined;

  function onDown(event: PointerEvent): void {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    const target = event.target as HTMLElement;
    if (target.closest('.pzd-icrop-bar')) return;
    event.preventDefault();
    event.stopPropagation();
    pinch.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pinch.size === 2) {
      const [a, b] = [...pinch.values()];
      pinchStart = { distance: Math.hypot(a!.x - b!.x, a!.y - b!.y) || 1, state };
      drag = undefined;
      return;
    }
    const handle = target.closest<HTMLElement>('.pzd-icrop-handle')?.dataset.handle ?? 'move';
    drag = { pointer: event.pointerId, x: event.clientX, y: event.clientY, start: state, handle };
    try { overlay.setPointerCapture(event.pointerId); } catch { /* synthetic events */ }
  }

  function onMove(event: PointerEvent): void {
    if (pinch.has(event.pointerId)) pinch.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pinch.size === 2 && pinchStart) {
      const [a, b] = [...pinch.values()];
      const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y) || 1;
      state = inlineZoom(source, pinchStart.state, distance / pinchStart.distance);
      place();
      return;
    }
    if (!drag || drag.pointer !== event.pointerId) return;
    const scale = host.geometry().scale || 1;
    const dx = (event.clientX - drag.x) / scale;
    const dy = (event.clientY - drag.y) / scale;
    state = drag.handle === 'move'
      ? inlinePan(source, drag.start, dx, dy, flipX, flipY)
      : inlineResize(source, drag.start, drag.handle, dx, dy, flipX, flipY);
    place();
  }

  function onUp(event: PointerEvent): void {
    pinch.delete(event.pointerId);
    if (pinch.size < 2) pinchStart = undefined;
    if (drag?.pointer === event.pointerId) drag = undefined;
  }

  function onWheel(event: WheelEvent): void {
    event.preventDefault();
    event.stopPropagation();
    state = inlineZoom(source, state, Math.exp(-event.deltaY / 400));
    place();
  }

  function onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(false); return; }
    if (event.key === 'Enter') { event.preventDefault(); event.stopPropagation(); close(true); return; }
    const arrows: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const step = arrows[event.key];
    if (!step) {
      if (event.key === '+' || event.key === '=') { state = inlineZoom(source, state, 1.1); place(); }
      if (event.key === '-') { state = inlineZoom(source, state, 1 / 1.1); place(); }
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const mm = event.shiftKey ? 2 : 0.5;
    state = inlinePan(source, state, step[0] * mm, step[1] * mm, flipX, flipY);
    place();
  }

  overlay.addEventListener('pointerdown', onDown);
  overlay.addEventListener('pointermove', onMove);
  overlay.addEventListener('pointerup', onUp);
  overlay.addEventListener('pointercancel', onUp);
  overlay.addEventListener('wheel', onWheel, { passive: false });
  overlay.addEventListener('keydown', onKey);
  picture.addEventListener('load', place);

  function close(applyIt: boolean): void {
    if (closed) return;
    closed = true;
    overlay.remove();
    host.hideLayer(id, false);
    if (applyIt) {
      const current = ctx.store.value();
      const live = current.layers.find((item) => item.id === id);
      // A photo replaced or removed underneath the crop is not this crop's.
      if (live?.kind === 'image' && live.assetId === layer.assetId) {
        try {
          const placed = applyDesignerCommand(ctx.rawConfig, current, { op: 'place', ids: [id], box: state.box });
          const cropped = applyDesignerCommand(ctx.rawConfig, placed, { op: 'imageCrop', ids: [id], crop: state.crop });
          ctx.dispatch({ type: 'set', answer: cropped, select: id, tag: `inline-crop:${id}:${Date.now()}` });
        } catch (error) {
          ctx.toast(error instanceof Error ? error.message : ctx.texts.cropTool, 'error');
        }
      }
    }
    onClosed();
  }

  place();
  frame.focus({ preventScroll: true });
  return { relayout: place, close };
}
